import React, { useCallback, useContext, useState } from 'react';
import useMemoObject from './useMemoObject';

// SvgContext stores the size of the svg which can change according to the browser size as well as
// the paddings to the inner chart area which is usually everything but the axes and labels.

// Note: Since pr-impact and the connected bubble chart we have two SvgCharts in one
//   ContextProvider. Fortunately only one of them needs the SvgContext but once we have two connected
//   charts, we have to "move" the svg context to the SvgChart component which was already implemented
//   with this requirement in mind.
// Challenge: the scales are currently defined outside the SvgChart but they need the SvgContext.
// Workaround: put the scale definition and every component inside SvgChart as a new child component
//   of SvgChart. Remove SvgContext handler from EventContext

const shouldUpdateIfNotEqual = (update: any, state = 0) => update !== undefined && update !== state;
export const shouldUpdateIfLarger = (update: any, state = 0) => update > state;
const shouldUpdateObject = (state: any, update: any) =>
  Boolean(Object.keys(update).find(key => shouldUpdateIfNotEqual(update[key], state[key])));
export const shouldUpdateObjectIfLarger = (state: any, update: any) =>
  Boolean(Object.keys(update).find(key => shouldUpdateIfLarger(update[key], state[key])));

const SvgContext = React.createContext<any>({});

export const SvgContextProvider = ({ children, minWidth, height }: any) => {
  const [width, setWidth] = useState(minWidth);
  const [padding, setPadding] = useState({ top: 0, right: 0, bottom: 0, left: 0 });

  const updatePaddingIfLarger = useCallback((update: { [name: string]: number }) => {
    setPadding(state => {
      const roundedValues: { [name: string]: number } = {};
      Object.keys(update).forEach(key => (roundedValues[key] = Math.round(update[key])));
      return shouldUpdateObjectIfLarger(state, roundedValues)
        ? { ...state, ...roundedValues }
        : state;
    });
  }, []);

  const updatePadding = useCallback((update: { [name: string]: number }) => {
    setPadding(state => {
      const roundedValues: { [name: string]: number } = {};
      Object.keys(update).forEach(key => (roundedValues[key] = Math.round(update[key])));
      return shouldUpdateObject(state, roundedValues) ? { ...state, ...roundedValues } : state;
    });
  }, []);

  const value = useMemoObject({
    minWidth,
    width,
    setWidth,
    height,
    updatePaddingIfLarger,
    ...padding,
    updatePadding,
  });

  return <SvgContext.Provider value={value}>{children}</SvgContext.Provider>;
};
export const useSvgContext = () => useContext(SvgContext); // variable
